import { useState, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { useForm, FormProvider } from "react-hook-form";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useLocation } from "react-router-dom";
import { mutate } from "swr";
import ReCAPTCHA from "react-google-recaptcha";

import { useFirebase } from "firebase";
import { fetchPublicAPI, NetworkError, usePOST } from "hooks/usePublicAPI";
import AuthLayout from "layouts/AuthLayout";
import { useAuthShellVariant } from "layouts/useAuthShellVariant";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import { inputClass } from "components/FormV2/fields/chrome";
import LoginGrantLogin from "pages/Login/LoginGrantLogin";
import {
  endedCodeSendFailureMessage,
  grantFailureKind,
  grantFailureMessage,
  failureFallbackFor,
  previousStepFallbackFor,
  smsRequestFailureMessage,
  smsVerifyFailureMessage,
  shouldRedirectAfterLogin,
} from "pages/Login/login-grant-flow";
import { SupportCallLine } from "pages/Login/GrantParts";
import PasswordVerification from "pages/Login/PasswordVerification";
import OtpVerification from "components/OtpVerification";
import type {
  SendTrigger,
  SubmitOutcome,
} from "components/OtpVerification/ended-code";
import BackButton from "components/Auth/BackButton";
import { formatPhoneNumberForAPI } from "helpers/phone";
import { readLoginGrantState } from "helpers/login-grant";
import {
  otpSessionFields,
  recordOtpSession,
  restartOtpSessions,
  type OtpSessions,
} from "helpers/otp-sessions";
import { freshRecaptchaToken } from "helpers/recaptcha";
import { safeRedirectPath } from "helpers/redirect";
import {
  apiErrorCode,
  getApiErrorMessage,
  getLookupErrorMessage,
} from "utils/errors";

type RecaptchaConfigResponse = {
  recaptchaSiteKey: string;
};

const lookupSchema = z.object({
  identifier: z.string().min(1, "Please enter an email or phone number"),
});

type LookupData = z.infer<typeof lookupSchema>;

type LoginStep =
  "lookup" | "password" | "sms_otp" | "magic_link_sent" | "invite_sent";
type LoginOption = {
  login_type: "PASSWORD" | "SMS_OTP" | "EMAIL_LINK";
  display_label?: string;
};

type LookupResponse = {
  options: LoginOption[];
};

type LoginPayload =
  | { email: string; password: string; phone?: never }
  | { phone: string; password: string; email?: never };
type LoginResponse = {
  success: boolean;
  custom_token?: string;
  error?: string;
  error_code?: string;
};

type MagicLinkPayload =
  { email: string; phone?: never } | { phone: string; email?: never };
type MagicLinkResponse = {
  message: string;
  error?: string;
  error_code?: string;
};

type RequestSmsOtpPayload = (
  { email: string; phone?: never } | { phone: string; email?: never }
) & { recaptchaToken: string };

type RequestSmsOtpResponse = {
  message: string;
  sessionInfo?: string; // Optional: Only present if user/phone found
  error?: string;
  error_code?: string;
};

type VerifyOtpPayload = {
  identifier: string; // Original email or phone
  sessionInfo: string;
  previousSessionInfo?: string;
  otpCode: string;
};

type VerifyOtpResponse = {
  success: boolean;
  custom_token?: string; // Present on successful verification
  error?: string;
  error_code?: string;
};

type ResendInvitePayload =
  { email: string; phone?: never } | { phone: string; email?: never };
type ResendInviteResponse = {
  success: boolean;
  invite_sent: boolean;
  method?: "email" | "sms";
};

const CLASSIC_IDENTIFIER_INPUT_CLASS =
  "block w-full rounded-none border-b border-gray-400 bg-transparent py-2 text-2xl font-bold text-soil transition-colors placeholder:text-soil placeholder:text-opacity-25 focus:border-cantelope focus:shadow-focus focus:outline-none";

const CLASSIC_SUBMIT_CLASS =
  "flex w-full items-center justify-center rounded-lg bg-cantelope px-6 py-3 font-bold text-soil transition-all hover:bg-soil hover:text-cantelope";

function getOptionLabel(type: LoginOption["login_type"]) {
  switch (type) {
    case "PASSWORD":
      return "Enter password";
    case "SMS_OTP":
      return "Text a code";
    case "EMAIL_LINK":
      return "Email a sign in link";
  }
}

function getVerificationText(step: LoginStep) {
  switch (step) {
    case "password":
      return {
        title: "Enter Password",
        description: "Please enter your password to continue",
      };
    case "sms_otp":
      return {
        title: "Enter Code",
        description: "Please enter the verification code",
      };
    case "magic_link_sent":
      return {
        title: "Check Your Email",
        description: "",
      };
    default:
      return {
        title: "Enter Code",
        description: "Please enter the verification code",
      };
  }
}

export default function LoginPage() {
  const [step, setStep] = useState<LoginStep>("lookup");
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [loginOptions, setLoginOptions] = useState<LoginOption[]>([]);
  const [identifier, setIdentifier] = useState("");
  const [sessions, setSessions] = useState<OtpSessions | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [firebaseRecaptchaSiteKey, setFirebaseRecaptchaSiteKey] = useState<
    string | null
  >(null);
  const [isFetchingRecaptchaKey, setIsFetchingRecaptchaKey] = useState(false);
  const [recaptchaKeyError, setRecaptchaKeyError] = useState<string | null>(
    null,
  );
  const methods = useForm<LookupData>({
    resolver: zodResolver(lookupSchema),
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, user, loading, signInWithCustomToken } = useFirebase();
  const restyled = useAuthShellVariant() === "quote_flow";
  const loginGrantState = readLoginGrantState(location.state);
  const loginGrant = loginGrantState?.loginGrant ?? null;

  const {
    trigger: triggerPasswordLogin,
    isMutating: isLoggingInWithPassword,
    error: passwordLoginError,
  } = usePOST<LoginResponse, NetworkError, LoginPayload>("/auth/login");

  const {
    trigger: triggerMagicLink,
    isMutating: isSendingMagicLink,
    error: magicLinkError,
  } = usePOST<MagicLinkResponse, NetworkError, MagicLinkPayload>(
    "/auth/request-magic-link",
  );

  const { trigger: triggerRequestSmsOtp, isMutating: isRequestingOtp } =
    usePOST<RequestSmsOtpResponse, NetworkError, RequestSmsOtpPayload>(
      "/auth/request-sms-otp",
    );

  const { trigger: triggerVerifyOtp, isMutating: isVerifyingOtp } = usePOST<
    VerifyOtpResponse,
    NetworkError,
    VerifyOtpPayload
  >("/auth/verify-sms-otp");

  const { trigger: triggerResendInvite } = usePOST<
    ResendInviteResponse,
    NetworkError,
    ResendInvitePayload
  >("/invites/resend");

  useEffect(() => {
    if (passwordLoginError) {
      console.error("Password Login API Error:", passwordLoginError);
      let errorCode: string | undefined = undefined;
      if (passwordLoginError instanceof NetworkError) {
        errorCode = (passwordLoginError.responseData as any)?.error_code;
      }
      setError(getApiErrorMessage(errorCode));
    }
  }, [passwordLoginError]);

  useEffect(() => {
    if (magicLinkError) {
      console.error("Magic Link API Error:", magicLinkError);
      let errorCode: string | undefined = undefined;
      if (magicLinkError instanceof NetworkError) {
        errorCode = (magicLinkError.responseData as any)?.error_code;
      }
      setError(getApiErrorMessage(errorCode));
    }
  }, [magicLinkError]);

  const emailLinkOffered = loginOptions.some(
    (option) => option.login_type === "EMAIL_LINK",
  );
  // The code screen has no emailed-link control of its own: the link is on
  // the sign-in method step behind it.
  const sendFallback =
    step === "sms_otp"
      ? previousStepFallbackFor(emailLinkOffered)
      : failureFallbackFor(emailLinkOffered);

  const smsRequestErrorMessage = (
    err: unknown,
    trigger: SendTrigger,
  ): string => {
    const wording =
      trigger === "code_ended"
        ? endedCodeSendFailureMessage
        : smsRequestFailureMessage;
    return wording(
      apiErrorCode(err),
      err instanceof NetworkError ? err.statusCode : undefined,
      sendFallback,
    );
  };

  const handleInviteResend = async (rawIdentifier: string) => {
    const isEmail = rawIdentifier.includes("@");
    try {
      let payload: ResendInvitePayload;
      if (isEmail) {
        payload = { email: rawIdentifier };
      } else {
        const formattedPhone = formatPhoneNumberForAPI(rawIdentifier);
        if (!formattedPhone) {
          setError(getLookupErrorMessage(404));
          return;
        }
        payload = { phone: formattedPhone };
      }

      const response = await triggerResendInvite(payload);
      if (response?.invite_sent) {
        setInfoMessage(
          isEmail
            ? `We found a pending invitation for your account. We've emailed a new invite link to ${rawIdentifier}. Please check your inbox to continue.`
            : `We found a pending invitation for your account. We've texted a new invite link to ${rawIdentifier}. Please check your phone to continue.`,
        );
        setStep("invite_sent");
      } else {
        setError(getLookupErrorMessage(404));
      }
    } catch (err) {
      if (!(err instanceof NetworkError)) {
        Sentry.captureException(err);
      }
      setError(getLookupErrorMessage(404));
    }
  };

  const handleLookup = async (data: LookupData) => {
    try {
      setError(null);
      setInfoMessage(null);
      setIdentifier(data.identifier);
      const isEmail = data.identifier.includes("@");
      const queryParam = isEmail
        ? `email=${encodeURIComponent(data.identifier)}`
        : `phone=${encodeURIComponent(data.identifier)}`;

      const responseData = await fetchPublicAPI<LookupResponse>(
        `/accounts/login/lookup?${queryParam}`,
      );
      setLoginOptions(responseData.options);
      setStep("lookup");
      setSessions(null);
    } catch (err) {
      if (err instanceof NetworkError) {
        if (err.statusCode === 404) {
          await handleInviteResend(data.identifier);
        } else {
          setError(getLookupErrorMessage(err.statusCode));
        }
      } else {
        Sentry.captureException(err);
        setError(getLookupErrorMessage(null));
      }
      setLoginOptions([]);
    }
  };

  useEffect(() => {
    if (loading || user || loginGrant) return;
    const stateIdentifier = (location.state as { identifier?: string } | null)
      ?.identifier;
    if (!stateIdentifier) return;
    methods.setValue("identifier", stateIdentifier);
    handleLookup({ identifier: stateIdentifier });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  useEffect(() => {
    if (!loading && shouldRedirectAfterLogin({ loginGrant, user })) {
      const params = new URLSearchParams(location.search);
      navigate(safeRedirectPath(params.get("redirect")), { replace: true });
    }
  }, [user, loading, navigate, location, loginGrant]);

  // --- Effect to fetch Firebase reCAPTCHA key when SMS option is available ---
  useEffect(() => {
    const shouldFetch = loginOptions.some((o) => o.login_type === "SMS_OTP");

    if (shouldFetch && !firebaseRecaptchaSiteKey && !isFetchingRecaptchaKey) {
      const fetchKey = async () => {
        setIsFetchingRecaptchaKey(true);
        setRecaptchaKeyError(null);
        try {
          const data = await fetchPublicAPI<RecaptchaConfigResponse>(
            "/auth/recaptcha-config",
          );
          if (data.recaptchaSiteKey) {
            setFirebaseRecaptchaSiteKey(data.recaptchaSiteKey);
          } else {
            throw new Error("recaptchaSiteKey not found in response");
          }
        } catch (err) {
          console.error("Failed to fetch reCAPTCHA config:", err);
          setRecaptchaKeyError(
            "Could not initialize login options. Please try again.",
          );
        } finally {
          setIsFetchingRecaptchaKey(false);
        }
      };
      fetchKey();
    }
  }, [loginOptions, firebaseRecaptchaSiteKey, isFetchingRecaptchaKey]);

  if (loginGrantState) {
    return (
      <LoginGrantLogin
        grant={loginGrantState}
        surface={loginGrantState.quoteId ? "account" : "login"}
      />
    );
  }

  if (loading) {
    return (
      <AuthLayout title="Loading..." description="">
        <div className="flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-cantelope border-t-transparent" />
        </div>
      </AuthLayout>
    );
  }

  if (user) {
    return null;
  }

  const handlePasswordLogin = async (password: string) => {
    setError(null);

    const isEmail = identifier.includes("@");
    let payload: LoginPayload;

    if (isEmail) {
      payload = { email: identifier, password };
    } else {
      const formattedPhone = formatPhoneNumberForAPI(identifier);
      if (!formattedPhone) {
        console.error(
          "Password login failed: Invalid phone number format provided.",
        );
        setError("Invalid phone number format.");
        return;
      }
      payload = { phone: formattedPhone, password };
    }

    try {
      const response = await triggerPasswordLogin(payload);

      if (response?.success && response.custom_token) {
        try {
          await signInWithCustomToken(auth, response.custom_token);
          mutate("/me");
        } catch (signInError: any) {
          console.error("Custom token sign-in failed:", signInError);
          Sentry.captureException(signInError);
          setError(
            "Login succeeded but failed to initialize session. Please try again.",
          );
        }
      } else {
        setError(response?.error || "Invalid credentials.");
      }
    } catch (err) {
      console.error("Password login trigger failed:", err);
    }
  };

  /** Sends a sign-in code; resolves true when one was sent. */
  const handleRequestSmsOtp = async (
    trigger: SendTrigger = "resend",
  ): Promise<boolean> => {
    setError(null);
    setInfoMessage(null);

    try {
      const token = await freshRecaptchaToken(recaptchaRef);
      if (!token.ok) {
        setError(grantFailureMessage("send_failed", sendFallback));
        return false;
      }
      const recaptchaToken = token.token;

      const isEmail = identifier.includes("@");
      let payload: RequestSmsOtpPayload;

      if (isEmail) {
        payload = { email: identifier, recaptchaToken };
      } else {
        const formattedPhone = formatPhoneNumberForAPI(identifier);
        if (!formattedPhone) {
          setError("Invalid phone number format.");
          return false;
        }
        payload = { phone: formattedPhone, recaptchaToken };
      }

      const response = await triggerRequestSmsOtp(payload);
      const sent = response?.sessionInfo;
      if (!sent) {
        setError(grantFailureMessage("send_failed", sendFallback));
        return false;
      }
      setSessions((held) =>
        trigger === "code_ended"
          ? restartOtpSessions(sent)
          : recordOtpSession(held, sent),
      );
      setInfoMessage(
        "Please check your phone for a 6-digit verification code.",
      );
      setStep("sms_otp");
      return true;
    } catch (error: any) {
      console.error("Error during OTP request or reCAPTCHA execution:", error);
      if (error instanceof NetworkError) {
        setError(smsRequestErrorMessage(error, trigger));
      } else {
        setError(grantFailureMessage("send_failed", sendFallback));
      }
      return false;
    }
  };

  const handleVerifyOtp = async (code: string): Promise<SubmitOutcome> => {
    setError(null);

    if (!sessions) {
      setError(
        "Verification session is missing or expired. Please try requesting a new code.",
      );
      setStep("lookup");
      return undefined;
    }

    const isEmail = identifier.includes("@");
    let requestIdentifier: string;

    if (isEmail) {
      requestIdentifier = identifier;
    } else {
      const formattedPhone = formatPhoneNumberForAPI(identifier);
      if (!formattedPhone) {
        setError("Invalid phone number format stored.");
        return undefined;
      }
      requestIdentifier = formattedPhone;
    }

    const payload: VerifyOtpPayload = {
      identifier: requestIdentifier,
      ...otpSessionFields(sessions),
      otpCode: code,
    };

    try {
      const response = await triggerVerifyOtp(payload);

      if (response?.success && response.custom_token) {
        try {
          await signInWithCustomToken(auth, response.custom_token);
          mutate("/me");
        } catch (signInError: any) {
          console.error("Custom token sign-in failed:", signInError);
          Sentry.captureException(signInError);
          setError(
            "Login succeeded but failed to initialize session. Please try again.",
          );
        }
      }
    } catch (err) {
      console.error("Error triggering verify OTP:", err);
      const statusCode =
        err instanceof NetworkError ? err.statusCode : undefined;
      if (grantFailureKind(apiErrorCode(err), statusCode) === "code_expired") {
        return "code_ended";
      }
      setError(
        smsVerifyFailureMessage(apiErrorCode(err), statusCode, sendFallback),
      );
    }
    return undefined;
  };

  const handleMagicLinkRequest = async () => {
    setError(null);
    setInfoMessage(null);

    const emailOption = loginOptions.find((o) => o.login_type === "EMAIL_LINK");
    const targetEmail = emailOption?.display_label;

    if (!targetEmail) {
      console.error(
        "Could not find email display_label in loginOptions for magic link message",
      );
      setError("An error occurred. Please try again.");
      return;
    }

    let payload: MagicLinkPayload;
    const isEmailInput = identifier.includes("@");

    if (isEmailInput) {
      payload = { email: identifier };
    } else {
      const formattedPhone = formatPhoneNumberForAPI(identifier);
      if (!formattedPhone) {
        setError("Invalid phone number format.");
        return;
      }
      payload = { phone: formattedPhone };
    }

    try {
      await triggerMagicLink(payload);

      setInfoMessage(
        `We've sent a login link to ${targetEmail}. Please check your inbox and click the link to sign in.`,
      );
      setStep("magic_link_sent");
    } catch (err) {
      console.error("Error triggering magic link:", err);
    }
  };

  const handleBack = () => {
    setError(null);
    setInfoMessage(null);
    recaptchaRef.current?.reset();
    setSessions(null);

    if (
      step === "lookup" ||
      step === "magic_link_sent" ||
      step === "invite_sent"
    ) {
      setLoginOptions([]);
      methods.reset();
      setIdentifier("");
      setStep("lookup");
    } else {
      setStep("lookup");
      methods.reset();
    }
  };

  if (step === "magic_link_sent") {
    return (
      <AuthLayout
        title={getVerificationText(step).title}
        description={infoMessage || "Please check your email."}
      >
        <div className="space-y-4 text-center">
          <BackButton onClick={handleBack} />
        </div>
      </AuthLayout>
    );
  }

  if (step === "invite_sent") {
    return (
      <AuthLayout
        title={
          identifier.includes("@") ? "Check Your Email" : "Check Your Phone"
        }
        description={infoMessage || "Please check your messages to continue."}
      >
        <div className="space-y-4 text-center">
          <BackButton onClick={handleBack} />
        </div>
      </AuthLayout>
    );
  }

  // The invisible widget backs every code request, the code screen's Resend
  // included, so each step that can send a code mounts it.
  const recaptchaWidget = firebaseRecaptchaSiteKey ? (
    <ReCAPTCHA
      ref={recaptchaRef}
      size="invisible"
      sitekey={firebaseRecaptchaSiteKey}
    />
  ) : null;

  if (loginOptions.length > 0) {
    if (step === "lookup") {
      // Disable button if key is loading or failed to load
      const isSmsDisabled = isFetchingRecaptchaKey || !!recaptchaKeyError;

      return (
        <AuthLayout
          title="Choose Sign In Method"
          description="Select how you'd like to sign in"
        >
          {recaptchaWidget}
          <div className="space-y-4">
            {loginOptions.map((option) => (
              <button
                key={option.login_type}
                onClick={() => {
                  setError(null);
                  setInfoMessage(null);
                  if (option.login_type === "SMS_OTP") {
                    handleRequestSmsOtp();
                  } else if (option.login_type === "EMAIL_LINK") {
                    handleMagicLinkRequest();
                  } else {
                    setStep(option.login_type.toLowerCase() as LoginStep);
                  }
                }}
                disabled={
                  isRequestingOtp ||
                  isSendingMagicLink ||
                  (option.login_type === "SMS_OTP" && isSmsDisabled)
                }
                className="flex w-full flex-col items-start rounded-lg border border-gray-200 p-4 text-left transition-colors hover:enabled:border-cantelope hover:enabled:bg-cantelope/5 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="text-lg font-semibold text-soil">
                  {(isRequestingOtp &&
                    option.login_type === "SMS_OTP" &&
                    "Sending code...") ||
                    (isSendingMagicLink &&
                      option.login_type === "EMAIL_LINK" &&
                      "Sending link...") ||
                    getOptionLabel(option.login_type)}
                </span>
                {option.display_label && (
                  <span className="text-sm text-gray-500">
                    {option.display_label}
                  </span>
                )}
              </button>
            ))}
            <BackButton onClick={handleBack} />
            {recaptchaKeyError && (
              <div className="rounded bg-red-100 p-3 text-sm text-red-700">
                {recaptchaKeyError}
              </div>
            )}
            {error && !recaptchaKeyError && (
              <div className="rounded bg-red-100 p-3 text-sm text-red-700">
                {error}
              </div>
            )}
            {infoMessage && (
              <div className="rounded bg-blue-100 p-3 text-sm text-blue-700">
                {infoMessage}
              </div>
            )}
            <SupportCallLine
              kind="first_call"
              surface="login"
              screen="sign_in"
            />
          </div>
        </AuthLayout>
      );
    }

    let verificationComponent;

    switch (step) {
      case "password":
        verificationComponent = (
          <PasswordVerification
            onSubmit={handlePasswordLogin}
            error={error}
            isLoading={isLoggingInWithPassword}
          />
        );
        break;
      case "sms_otp":
        verificationComponent = (
          <OtpVerification
            type="sms"
            destinationDisplay={
              loginOptions.find((o) => o.login_type === "SMS_OTP")
                ?.display_label || identifier
            }
            onSubmit={handleVerifyOtp}
            onResend={handleRequestSmsOtp}
            error={error}
            isLoading={isVerifyingOtp}
          />
        );
        break;
    }

    if (verificationComponent) {
      return (
        <AuthLayout
          title={getVerificationText(step).title}
          description={infoMessage || getVerificationText(step).description}
        >
          {recaptchaWidget}
          <div className="space-y-4">
            {verificationComponent}
            <BackButton onClick={handleBack} />
          </div>
        </AuthLayout>
      );
    } else {
      console.warn(`Unexpected step encountered: ${step}`);
      return null;
    }
  }

  return (
    <AuthLayout
      title="Welcome Back"
      description="Sign in to your After account"
    >
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(handleLookup)}
          className="space-y-6"
        >
          <div>
            <input
              type="text"
              autoComplete="username"
              {...methods.register("identifier")}
              className={
                restyled
                  ? inputClass(!!methods.formState.errors.identifier)
                  : CLASSIC_IDENTIFIER_INPUT_CLASS
              }
              placeholder="Enter email or phone"
              autoFocus
            />
            <div className="mt-1 text-sm text-gray-500">
              Standard SMS rates may apply
            </div>
            {methods.formState.errors.identifier && (
              <div className="mt-1 text-sm text-red-600">
                {methods.formState.errors.identifier.message}
              </div>
            )}
          </div>

          {error && (
            <div className="rounded bg-red-100 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            className={
              restyled
                ? `${CONTINUE_BUTTON_CLASS} w-full`
                : CLASSIC_SUBMIT_CLASS
            }
            disabled={methods.formState.isSubmitting}
          >
            <LockClosedIcon className="mr-2 h-5 w-5" />
            {methods.formState.isSubmitting ? "Checking..." : "Sign in"}
          </button>
        </form>
      </FormProvider>

      <div className="mt-8 border-t border-gray-200 pt-4 text-center text-xs text-gray-500">
        Invisible reCAPTCHA by Google{" "}
        <a
          href="https://www.google.com/intl/en/policies/privacy/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block underline decoration-dotted hover:decoration-solid"
        >
          Privacy Policy
        </a>{" "}
        and{" "}
        <a
          href="https://www.google.com/intl/en/policies/terms/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block underline decoration-dotted hover:decoration-solid"
        >
          Terms of Use
        </a>
      </div>
    </AuthLayout>
  );
}
